// @flow
import * as React from 'react';

import type {
  BuilderMode,
  CustomerSignupSetting,
  BuilderModeSpecifications,
  ProductImportTemplateSpecification,
} from 'modules/settings';
import type { AutomaticShipmentsSetting } from 'modules/automatic-shipments';
import { DEFAULT_LABEL_HIGH } from 'modules/availability';
import type { HighlightSpec } from 'modules/highlight';
import type { LabelKey } from 'modules/whiteboard/has-products';
import type { ROA, ROE } from 'modules/type';
import type { EmbellishmentConfig } from 'modules/product-embellishment-provider';
import type { PriceDisplayPreference } from 'modules/monetary-amount';

export type ExportAvailability = boolean | 'both';

export type CatalogCardLogos = $ReadOnly<{ [string]: string }>;

export type AccountHistoryAdministrator = $ReadOnlyArray<{
  disabledMenus: $ReadOnlyArray<string>,
  soldTo: string,
}>;

export type QuantityFormLabelSetting = ROE<{
  multiLineLabel?: boolean,
  altNameLabelGridWidth?: number,
  altNameLabelForgoEllipsize?: boolean,
}>;

export type SettingsParams = $ReadOnly<{|
  clientName: string,
  currencyCode: string,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  priceType: PriceDisplayPreference,
  whiteboardAllowedLabels: ROA<LabelKey>,
  whiteboardTagLabels: string[],
  enableStrikethrough: boolean,
  hideProductDataInUI: {
    product_number: boolean,
    variation_name: boolean,
    variation_number: boolean,
    stock_item_name: boolean,
  },
  defaultImage: string,
  usesPageLevelDiscounts: boolean,
  usesAvailabilityWarnings: boolean,
  availabilityRestrictsValidity: boolean,
  enforceReleaseDate?: boolean,
  externalBarcodeApi: boolean,
  restrictShipToByDivision: boolean,
  usesExternalPrices: boolean,
  showExternalPriceWhenEqualToWholesalePrice: boolean,
  externalPriceTotalsShouldFallBackToWholesalePrice: boolean,
  alwaysShowExternalPrice: boolean,
  exportAvailability: ExportAvailability,
  importClientFile: boolean | string,
  importOfflineOrder: boolean,
  automaticShipments: AutomaticShipmentsSetting,
  customerSignup: CustomerSignupSetting,
  clientLogo: string,
  isTouchscreen: boolean,
  embellishmentsActionableForRepsOnly: boolean,
  embellishmentStrategies: $ReadOnlyArray<EmbellishmentConfig>,
  defaultOrderBuilderMode: BuilderMode,
  defaultOrderBuilderModeForReps?: BuilderMode,
  userDefaultOrderBuilderMode?: BuilderMode,
  builderModeSpecifications: BuilderModeSpecifications,
  altNameSeparator: string,
  catalogCardLogos: CatalogCardLogos,
  tablet: boolean,
  includeOTSColumn: boolean,
  atsLabelHigh: string,
  defaultAtsLabelMax: number,
  highlightSpecs: $ReadOnlyArray<HighlightSpec>,
  hideNotOrderable: boolean,
  usesProductLevelAts: boolean,
  sendSupportRequest: () => void,
  rep2repsharing: boolean,
  onlyCustomerTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  exceptCustomerTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  onlyCatalogTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  exceptCatalogTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  secondaryCatalogTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  newOrderDefaultCatalogTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  tertiaryCatalogTags?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  usesCatalogTagFilters: boolean,
  hideWarehousesWithZeroInventory: boolean,
  disableQuantityInput: boolean,
  productReferenceKeyPaths: null | $ReadOnlyArray<string>,
  forecastingAllowedUserTypes: $ReadOnlyArray<'rep' | 'dealer'>,
  productImportTemplates: $ReadOnlyArray<ProductImportTemplateSpecification>,
  newOrderSortCatalogsByName?: boolean,
  forceCustomerSelection: boolean,
  catalogsRepresentBrands: boolean,
  quantityTableSizeSort?: 'default' | 'interleavedByAltName',
  skipNewOrderFormModal: boolean,
  tenantId: string,
  variationFormListLargeHeroImage?: boolean,
  truncateSizesLength: number,
  accountHistoryAdministrator?: AccountHistoryAdministrator,
  defaultPaperSize: string,
  enterQuantities: boolean,
  hideAllProductsButton: boolean,
  hideMoreDetailsButton: boolean,
  hideEditIconsInCartOverview: boolean,
  quantityFormLabel?: QuantityFormLabelSetting,
  inventoryCheckMandatory?: boolean,
  pageProductBoxes?: boolean,
  includeLocationNumberInSelect: boolean,
  directoryMode: boolean,
  viewProducts: boolean,
  algoliaConfig: ?ROE<{
    applicationId: string,
    searchApiKey: string,
    brandsIndex: string,
  }>,
  brandSummaryTag: string,
  searchResultsFilteredByAvailability: {
    enabled: boolean,
    availabilityRanges: $ReadOnlyArray<{
      label: string,
      daysWithin: number,
    }>,
  },
  apolloClientOptions: $ReadOnly<{|
    uri: string | null,
    bearerToken: string | null,
  |}>,
  ordersDashboardWidgetOrderNumberColumn?: boolean,
  includeCustomerNumberInSelect: boolean,
  campaignsEnabled: boolean,
  usesExternalOrderHistory: boolean,
  hideAccountSettings: boolean,
  hideProductSearch: boolean,
  thoughtspotSettings: {
    url: string | null,
    repLiveboardId: string | null,
    dealerLiveboardId: string | null,
  },
  includeThoughtspotMetricsForTheRep: boolean,
  includeThoughtspotMetricsForTheDealer: boolean,
  shipmentsByAvailableStock?: boolean,
  showZeroValueDiscounts?: boolean,
  excludeZeroQuantityInEmail: boolean,
  orderNameRegExp: string,
  orderStatus: $ReadOnly<{|
    customFilterFields?: $ReadOnlyArray<string>,
    optionalViewModes?: $ReadOnlyArray<string>,
    repMode?: string,
    hideUpdate: boolean,
    hideCancel: boolean,
    detailModeOverrideColumns: $ReadOnlyArray<string>,
    orderModeOverrideColumns: $ReadOnlyArray<string>,
    hideColumns: $ReadOnlyArray<string>,
  |}>,
  enableExploreHomepage: boolean,
|}>;

export const SettingsContext: React$Context<SettingsParams> =
  React.createContext<SettingsParams>({
    clientName: 'Scramble',
    currencyCode: 'USD',
    wholesalePriceGroup: 'wholesale',
    retailPriceGroup: 'retail',
    priceType: 'wholesale',
    whiteboardAllowedLabels: ['product_name', 'product_number', 'color_code'],
    whiteboardTagLabels: [],
    enableStrikethrough: false,
    hideProductDataInUI: {
      product_number: false,
      variation_name: false,
      variation_number: false,
      stock_item_name: false,
    },
    defaultImage: 'missing.svg',
    usesPageLevelDiscounts: false,
    usesAvailabilityWarnings: false,
    availabilityRestrictsValidity: false,
    enforceReleaseDate: false,
    externalBarcodeApi: false,
    restrictShipToByDivision: false,
    usesExternalPrices: false,
    showExternalPriceWhenEqualToWholesalePrice: false,
    externalPriceTotalsShouldFallBackToWholesalePrice: false,
    alwaysShowExternalPrice: false,
    exportAvailability: false,
    importClientFile: false,
    importOfflineOrder: false,
    automaticShipments: [],
    customerSignup: {
      regions: [],
      countries: { ...null },
      states: { ...null },
      brands: [],
    },
    clientLogo: 'missing.svg',
    isTouchscreen: false,
    embellishmentsActionableForRepsOnly: false,
    embellishmentStrategies: [],
    defaultOrderBuilderMode: 'advanced',
    defaultOrderBuilderModeForReps: undefined,
    userDefaultOrderBuilderMode: undefined,
    builderModeSpecifications: [],
    altNameSeparator: '\u00D7',
    catalogCardLogos: { ...null },
    tablet: false,
    includeOTSColumn: false,
    atsLabelHigh: DEFAULT_LABEL_HIGH,
    defaultAtsLabelMax: 99,
    highlightSpecs: [],
    hideNotOrderable: false,
    usesProductLevelAts: false,
    sendSupportRequest: () => {},
    rep2repsharing: false,
    hideWarehousesWithZeroInventory: false,
    disableQuantityInput: false,
    productReferenceKeyPaths: null,
    forecastingAllowedUserTypes: ['rep'],
    productImportTemplates: [],
    onlyCatalogTags: [],
    exceptCatalogTags: [],
    secondaryCatalogTags: [],
    newOrderDefaultCatalogTags: [],
    tertiaryCatalogTags: [],
    usesCatalogTagFilters: false,
    newOrderSortCatalogsByName: false,
    forceCustomerSelection: false,
    catalogsRepresentBrands: false,
    quantityTableSizeSort: 'default',
    skipNewOrderFormModal: false,
    tenantId: 'MISSING TENANT ID',
    variationFormListLargeHeroImage: false,
    truncateSizesLength: 99,
    accountHistoryAdministrator: [],
    defaultPaperSize: 'northAmerican',
    enterQuantities: false,
    hideAllProductsButton: false,
    hideMoreDetailsButton: false,
    hideEditIconsInCartOverview: false,
    pageProductBoxes: false,
    includeLocationNumberInSelect: false,
    directoryMode: false,
    viewProducts: false,
    algoliaConfig: null,
    brandSummaryTag: 'Product Categories',
    searchResultsFilteredByAvailability: {
      enabled: false,
      availabilityRanges: [
        { label: 'In 2 weeks', daysWithin: 14 },
        { label: 'Available at any day in the future', daysWithin: 0 },
        { label: 'No availability check', daysWithin: 0 },
      ],
    },
    apolloClientOptions: {
      uri: null,
      bearerToken: null,
    },
    ordersDashboardWidgetOrderNumberColumn: false,
    includeCustomerNumberInSelect: false,
    campaignsEnabled: false,
    usesExternalOrderHistory: false,
    hideAccountSettings: false,
    hideProductSearch: false,
    thoughtspotSettings: {
      url: null,
      repLiveboardId: null,
      dealerLiveboardId: null,
    },
    includeThoughtspotMetricsForTheRep: false,
    includeThoughtspotMetricsForTheDealer: false,
    shipmentsByAvailableStock: false,
    showZeroValueDiscounts: false,
    excludeZeroQuantityInEmail: false,
    orderNameRegExp: '',
    orderStatus: {
      hideUpdate: true,
      hideCancel: true,
      detailModeOverrideColumns: [
        'erp_order_number',
        'line_item_number',
        'product_number',
        'product_name',
        'variation_name',
        'size_name',
        'account_number',
        'location_number',
        'quantity',
        'amount',
        'state',
        'invoice_number',
        'tracking_number',
      ],
      orderModeOverrideColumns: [
        'erp_order_number',
        'po',
        'order_date',
        'ship_date',
        'quantity',
        'amount',
      ],
      hideColumns: [],
    },
    enableExploreHomepage: false,
  });

export const SettingsProvider = SettingsContext.Provider;
export const SettingsConsumer = SettingsContext.Consumer;

export const withSettings = <
  Props: { +forwardedRef?: (mixed) => ?HTMLElement },
>(
  Component: React.AbstractComponent<React.Config<Props, any>>,
): (React.AbstractComponent<
  React.Config<
    $Diff<
      Props,
      $ReadOnly<{
        clientName: string | void,
        currencyCode: string | void,
        wholesalePriceGroup: string | void,
        retailPriceGroup: string | void,
        priceType: PriceDisplayPreference | void,
        whiteboardAllowedLabels: ROA<LabelKey> | void,
        whiteboardTagLabels: string[] | void,
        defaultImage: string | void,
        usesPageLevelDiscounts: boolean | void,
        usesAvailabilityWarnings: boolean | void,
        availabilityRestrictsValidity: boolean | void,
        enforceReleaseDate: boolean | void,
        externalBarcodeApi: boolean | void,
        restrictShipToByDivision: boolean | void,
        usesExternalPrices: boolean | void,
        showExternalPriceWhenEqualToWholesalePrice: boolean | void,
        externalPriceTotalsShouldFallBackToWholesalePrice: boolean | void,
        alwaysShowExternalPrice: boolean | void,
        exportAvailability: ExportAvailability | void,
        importClientFile: boolean | string | void,
        importOfflineOrder: boolean | void,
        automaticShipments: AutomaticShipmentsSetting | void,
        customerSignup: CustomerSignupSetting | void,
        clientLogo: string | void,
        isTouchscreen: boolean | void,
        embellishmentsActionableForRepsOnly: boolean | void,
        embellishmentStrategies: $ReadOnlyArray<EmbellishmentConfig> | void,
        forwardedRef?: (mixed) => ?HTMLElement,
        defaultOrderBuilderMode: BuilderMode | void,
        defaultOrderBuilderModeForReps?: BuilderMode,
        builderModeSpecifications: BuilderModeSpecifications | void,
        altNameSeparator: string | void,
        catalogCardLogos?: CatalogCardLogos,
        tablet: boolean | void,
        includeOTSColumn: boolean | void,
        atsLabelHigh: string | void,
        defaultAtsLabelMax: number | void,
        highlightSpecs: $ReadOnlyArray<HighlightSpec> | void,
        hideNotOrderable: boolean | void,
        usesProductLevelAts: boolean | void,
        sendSupportRequest: (() => void) | void,
        rep2repsharing: boolean | void,
        onlyCustomerTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        exceptCustomerTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        onlyCatalogTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        exceptCatalogTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        secondaryCatalogTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        newOrderDefaultCatalogTags: $ReadOnlyArray<
          $ReadOnlyArray<string>,
        > | void,
        tertiaryCatalogTags: $ReadOnlyArray<$ReadOnlyArray<string>> | void,
        forceCustomerSelection: boolean | void,
        usesCatalogTagFilters: boolean | void,
        hideWarehousesWithZeroInventory: boolean | void,
        disableQuantityInput: boolean | void,
        forecastingAllowedUserTypes: $ReadOnlyArray<'rep' | 'dealer'> | void,
        productImportTemplates: void | $ReadOnlyArray<ProductImportTemplateSpecification>,
        newOrderSortCatalogsByName: boolean | void,
        catalogsRepresentBrands: boolean | void,
        quantityTableSizeSort: 'default' | 'interleavedByAltName' | void,
        skipNewOrderFormModal: boolean | void,
        tenantId: string | void,
        variationFormListLargeHeroImage: boolean | void,
        truncateSizesLength: number | void,
        accountHistoryAdministrator: AccountHistoryAdministrator | void,
        defaultPaperSize: string | void,
        enterQuantities: boolean | void,
        hideAllProductsButton: boolean | void,
        hideMoreDetailsButton: boolean | void,
        hideEditIconsInCartOverview: boolean | void,
        inventoryCheckMandatory: boolean | void,
        pageProductBoxes: boolean | void,
        includeLocationNumberInSelect: boolean | void,
        directoryMode: boolean | void,
        viewProducts: boolean | void,
        algoliaConfig: SettingsParams['algoliaConfig'],
        brandSummaryTag: string | void,
        apolloClientOptions: $ReadOnly<{|
          uri: string | null,
          bearerToken: string | null,
        |}> | void,
        ordersDashboardWidgetOrderNumberColumn: boolean | void,
        campaignsEnabled: boolean | void,
        usesExternalOrderHistory: boolean | void,
        includeCustomerNumberInSelect: boolean | void,
        thoughtspotSettings: {
          url: string | null,
          repLiveboardId: string | null,
          dealerLiveboardId: string | null,
        } | void,
        includeThoughtspotMetricsForTheRep: boolean | void,
        includeThoughtspotMetricsForTheDealer: boolean | void,
        shipmentsByAvailableStock: boolean | void,
        showZeroValueDiscounts: boolean | void,
      }>,
    >,
    any,
  >,
>) => {
  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  function WrapperComponent({
    forwardedRef,
    ...passedThroughProps
  }: $ReadOnly<{
    ...Props,
    forwardedRef: React.Ref<any>,
  }>) {
    return (
      <SettingsConsumer>
        {(props: SettingsParams) => (
          // $FlowFixMe[cannot-spread-inexact]
          <Component {...passedThroughProps} {...props} ref={forwardedRef} />
        )}
      </SettingsConsumer>
    );
  }
  return React.forwardRef((props, ref) => (
    <WrapperComponent {...props} forwardedRef={ref} />
  ));
};
